import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { useDemo } from "../context/DemoContext";
import { PRODUCTS } from "../data/content";

export default function Products() {
  const { openDemo } = useDemo();
  return (
    <>
      <SEO
        title="Products — MoveOS, myBantu, LearnSphere, CraneSoftwares | ZasyaOnline"
        description="ZasyaOnline's product portfolio: MoveOS for transport and fleets, myBantu AI call centre, LearnSphere enterprise learning, and CraneSoftwares for crane rental operations."
        path="/products"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="products-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <span className="eyebrow">Products</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Products we build, run and stand behind.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Four products, each built for the operational reality of its industry —
              and each one a demonstration of the engineering we bring to every
              consulting engagement.
            </p>
          </Reveal>
        </div>
      </section>
      <Marquee items={PRODUCTS.map((p) => p.name)} />
      <div data-testid="products-fullscreen-sections">
        {PRODUCTS.map((p, i) => (
          <section
            key={p.id}
            className={`flex min-h-screen items-center py-16 sm:py-20 ${i % 2 === 1 ? "bg-white" : ""}`}
            data-testid={`products-section-${p.id}`}
          >
            <div className="container-x w-full">
              <Reveal>
                <article
                  className="grid items-center gap-10 lg:grid-cols-12"
                  data-testid={`products-row-${p.id}`}
                >
                  <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <Link to={p.path} className="group block">
                      <div className="clip-frame overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5">
                        <img
                          src={p.image}
                          alt={p.imageAlt}
                          className="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-[52vh]"
                          loading="lazy"
                        />
                      </div>
                    </Link>
                  </div>
                  <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    {p.logo && (
                      <img
                        src={p.logo}
                        alt={`${p.name} logo`}
                        className="mb-6 h-12 w-auto max-w-[240px] object-contain object-left sm:h-14"
                        data-testid={`products-logo-${p.id}`}
                      />
                    )}
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal-700">
                      {p.category}
                    </span>
                    <h2 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                      {p.name}
                    </h2>
                    <p className="mt-2 font-display text-xl font-semibold text-blue-700">
                      {p.headline}
                    </p>
                    <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
                      {p.description}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to={p.path} className="btn-primary" data-testid={`products-learn-more-${p.id}`}>
                        Learn about {p.name} <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                      <a
                        href={p.url}
                        className="btn-secondary"
                        data-testid={`products-visit-${p.id}`}
                      >
                        Visit {p.name} website <ArrowUpRight size={15} aria-hidden="true" />
                      </a>
                      <button
                        type="button"
                        onClick={() => openDemo(p.id)}
                        className="btn-secondary !border-transparent !text-teal-700 hover:!border-teal-600"
                        data-testid={`products-demo-${p.id}`}
                      >
                        Book a Demo
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            </div>
          </section>
        ))}
      </div>
      <CTASection
        title="Not sure which product fits?"
        body="Describe your operation and we'll point you to the right product — or tell you honestly if a consulting engagement makes more sense."
      />
    </>
  );
}
